import { definePreset } from '@primevue/themes'
import Aura from '@primevue/themes/aura'

const SlicealPreset = definePreset(Aura, {
  semantic: {
    primary: {
      25: '#EEEFFF',
      50: '#EEEFFF',
      100: '#DEE2FF',
      200: '#C3C8FF',
      300: '#9FA4FF',
      400: '#7E79FF',
      500: '#6755FB',
      600: '#5C3CF0',
      700: '#502ED4',
      800: '#4228AC',
      900: '#23184E',
      950: '#23184E',
    },

    success: {
      50: '#EEFDF7',
      100: '#D8F9EB',
      200: '#B6F2DA',
      300: '#86E6C2',
      400: '#57D8A8',
      500: '#2FC48D',
      600: '#1EA775',
      700: '#17835C',
      800: '#146A4B',
      900: '#12563D',
    },

    warning: {
      50: '#FFF9EB',
      100: '#FFF0C9',
      200: '#FFE29A',
      300: '#FFD166',
      400: '#FFBD33',
      500: '#F7A600',
      600: '#D98D00',
      700: '#B97500',
      800: '#9B6200',
      900: '#7F5100',
    },

    danger: {
      50: '#FFF3F3',
      100: '#FFE2E2',
      200: '#FFC8C8',
      300: '#FFA1A1',
      400: '#FF7070',
      500: '#F04444',
      600: '#D92D2D',
      700: '#B42323',
      800: '#8F1C1C',
      900: '#761818',
    },

    info: {
      50: '#EEF5FF',
      100: '#D9E9FF',
      200: '#BCD8FF',
      300: '#91C0FF',
      400: '#63A5FF',
      500: '#3D89F5',
      600: '#2C72DB',
      700: '#225CC0',
      800: '#1E4A9A',
      900: '#1D3F7F',
    },
    colorScheme: {
      light: {
        text: {
          color: '#0A0A0A', // overrides --p-text-color
          mutedColor: '#414651', // overrides --p-text-muted-color
        },
      },
      dark: {
        text: {
          color: '#F9FAFB',
          mutedColor: '#D1D5DB',
        },
        surface: {
          0: '#0E1014', // page background
          50: '#15181D', // subtle elevated bg
          100: '#1B1F26', // card / chip bg
          200: '#262B33', // borders subtle / raised
          300: '#333944',
          400: '#525964',
          500: '#71717A',
          600: '#A1A1AA',
          700: '#D4D4D8',
          800: '#E4E4E7',
          900: '#F4F4F5',
          950: '#FAFAFA',
        },
        primary: {
          25: '#1A1640',
          50: '#1F1A4A',
          100: '#2A2363',
          200: '#3A2F8A',
        },
        success: {
          50: '#0F2A1E',
          100: '#143A29',
          200: '#1A5239',
        },
        warning: {
          50: '#3A2A0A',
          100: '#4D3A0F',
          200: '#6B521A',
          // Dark counterpart for the amber hairline on action-required tiles.
          // Without it, `warning-300` fell through to its light value (#FFD166)
          // and burned a bright edge onto a dark `warning-50` surface.
          300: '#8A6B22',
        },
        danger: {
          50: '#3A1414',
          100: '#4D1A1A',
          200: '#6B2424',
        },
        info: {
          50: '#0F1F3A',
          100: '#152A52',
          200: '#1D3C7A',
        },
        content: {
          borderColor: '#262B33',
        },
      },
    },
  },

  primitive: {
    borderRadius: {
      sm: '6px',
      md: '10px',
      lg: '14px',
      xl: '20px',
    },
    slicealColors: {
      lavender: '#EBE9FF',
      primaryDeep: '#24195F',
      primaryGlow: '#7A6BFF',
      softPinkWash: '#FDF2F9',
      softPinkWashDark: '#3A2038',
      neoMintWash: '#E8FFF7',
      neoMintWashDark: '#0F332A',
      textSubtle: '#A4A7AE',
      textSubtleDark: '#9CA3AF',
    },
    fontFamily:
      '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  extend: {
    sliceal: {
      landingNavbar: '{slicealColors.lavender}',
      signUpRight: '{slicealColors.lavender}',
      // The design system's "Shadow/xs" — the hairline lift on inline rows and
      // input-shaped surfaces (upload row, toggle row). Emitted as
      // `--p-sliceal-shadow-xs`; the preset is the only place the raw colour
      // lives, so raising the elevation is one edit rather than a grep.
      shadowXs: '0 1px 2px 0 rgba(10, 13, 18, 0.05)',
    },
    // Tokens whose *value* has to change between the two themes, not just their
    // resolved colour — each one would otherwise be invisible or wrong in dark
    // mode. Anything scheme-independent belongs in `sliceal` above.
    //
    // `softPinkWash` — the design system's "Soft Pink/50", the warm wash behind
    // a *selected* card's corner blobs, where resting cards get the cool
    // Brand/25. The only warm hue in the system, and only ever a 12px-blurred
    // tint, so it reads as "this one is lit up" rather than a second brand
    // colour. Emitted as `--p-sliceal-soft-pink-wash`. Dark swaps to a plum of
    // matching luminance — a near-white pink would burn a hole in a dark card
    // the way `primary-50` never does.
    //
    // `neoMintWash` — the design system's "Neo Mint/50", the cool counterpart to
    // `softPinkWash`: the 20px-blurred blob tucked behind a card's top-right
    // corner. Emitted as `--p-sliceal-neo-mint-wash`. Dark drops to a deep teal of
    // card-level luminance, because a near-white mint blob on a `surface-0` dark
    // card reads as a blown-out highlight rather than a tint.
    //
    // `shadowAmbient` / `shadowAmbientTight` — the ambient card halo, an
    // all-round glow (no offset) on the listing wizard's step cards, which carry
    // it *instead of* a border. The design tightens the blur rather than
    // deepening the colour to mark the emphasised card, hence the pair. Because
    // the glow is the card's only edge, the near-black light-mode value renders
    // nothing on a dark surface and the card would simply vanish; dark therefore
    // leads with a hairline light ring and keeps a glow behind it for depth.
    //
    // `textSubtle` — the design system's "Gray/400", a third text tier *below*
    // `--p-text-muted-color`. Muted (Gray/700) is still a readable label colour;
    // this one is for the caption a KPI tile hangs under its figure, where the
    // whole point is that it recedes. Emitted as `--p-sliceal-text-subtle`.
    // Dark can't reuse the light value — #A4A7AE on a near-black card is louder
    // than the muted tier it is meant to sit under — so it steps one notch down
    // from dark's muted grey instead of one notch up.
    colorScheme: {
      light: {
        sliceal: {
          softPinkWash: '{slicealColors.softPinkWash}',
          neoMintWash: '{slicealColors.neoMintWash}',
          textSubtle: '{slicealColors.textSubtle}',
          shadowAmbient: '0 0 10px rgba(10, 13, 18, 0.2)',
          shadowAmbientTight: '0 0 5px rgba(10, 13, 18, 0.2)',
        },
      },
      dark: {
        sliceal: {
          softPinkWash: '{slicealColors.softPinkWashDark}',
          neoMintWash: '{slicealColors.neoMintWashDark}',
          textSubtle: '{slicealColors.textSubtleDark}',
          shadowAmbient: '0 0 0 1px rgba(255, 255, 255, 0.08), 0 0 10px rgba(0, 0, 0, 0.5)',
          shadowAmbientTight: '0 0 0 1px rgba(255, 255, 255, 0.08), 0 0 5px rgba(0, 0, 0, 0.5)',
        },
      },
    },
  },
})

export default SlicealPreset
